<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";
import AppModal from "@/components/ui/AppModal.vue";
import { dateTimeFormat } from "@/commons/composables/transformations.ts";
import { useRoute } from "vue-router";
import jwtService from "@/app/services/jwt.service";
import { EntityType } from "@/domains/home/services/nir/configurations/types/forms.ts";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import { useNotificationsStore } from "@/app/notify/notifications.ts";
import { useRegistryStore } from "@/domains/home/services/registry/stores";

const route = useRoute();
const notify = useNotificationsStore()

const store = useInstitutionStore()
const formsStore = useFormsStore()
const registryStore = useRegistryStore()

const activeTab = ref('contact')
const loading = ref(false)
const loadingComplianceStatuses = ref(false)

const showComplianceStatusForm = ref(false)
const editingComplianceStatusId = ref<string | null>(null)
const savingComplianceStatus = ref(false)
const complianceStatusForm = ref({ status: '', description: '' })

// Affiliation attachments
const availableFileTypes = [
  { value: 'application/pdf', label: 'PDF' },
  { value: 'image/png', label: 'PNG' },
  { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'DOCX' },
]

const showAttachmentForm = ref(false)
const editingAttachment = ref<string | null>(null)
const removingAttachmentId = ref<string | null>(null)
const savingAttachment = ref(false)
const attachmentForm = ref({
  label: '',
  description: '',
  isMandatory: false,
  maxSize: 5,
  fileType: '',
})

const approving = ref(false)
const rejecting = ref(false)
const enablingDirectAdmission = ref(false)
const disablingDirectAdmission = ref(false)
const enablingInterimErn = ref(false);
const disablingInterimErn = ref(false);
const enablingRegisterExisting = ref(false);
const disablingRegisterExisting = ref(false);
const showApproveConfirm = ref(false)
const showRejectConfirm = ref(false)
const showEnableDirectAdmissionConfirm = ref(false)
const showDisableDirectAdmissionConfirm = ref(false)
const showEnableInterimErnConfirm = ref(false);
const showDisableInterimErnConfirm = ref(false);
const showEnableRegisterExistingConfirm = ref(false);
const showDisableRegisterExistingConfirm = ref(false);

onMounted(() => {
  fetch()
})

function fetch(){
  loading.value = true
  let id = route.params.id as string
  store.fetchInstitution(id)
      .then(() => {
        loading.value = false
      })

  formsStore.fetchEntityTypes('')
  formsStore.fetchAvailableEntityTypes()
  formsStore.fetchAffiliationAttachments(route.params.id as string)
}

const approve = () => {
  showApproveConfirm.value = true
}

const confirmApprove = () => {
  approving.value = true
  let id = route.params.id as string
  let userId  = jwtService.getUser()["userId"]
  let payload = {
    institutionId: id,
    userId: userId
  }
  store.approveInstitution(payload)
      .then(() => {
        approving.value = false
        showApproveConfirm.value = false
        notify.success("Institution approved successfully")
        fetch()
      })
      .catch(() => {
        approving.value = false
        showApproveConfirm.value = false
        notify.error("Failed to approve institution")
      })
}

const confirmReject = () => {
  rejecting.value = true
  let id = route.params.id as string
  store.rejectInstitution(id)
      .then(() => {
        rejecting.value = false
        showRejectConfirm.value = false
        notify.success("Institution rejected successfully")
      })
      .catch(() => {
        rejecting.value = false
        notify.error("Failed to reject institution")
      })
}

const organizedEntityTypes = computed(() => {
  if (!formsStore.entityTypes) return []

  const rootTypes = formsStore.entityTypes.filter((type) => !type.parentId)

  function buildHierarchy(parent: EntityType, level = 0): any[] {
    const children = formsStore.entityTypes?.filter((type) => type.parentId === parent.id) || []
    const result = [{ ...parent, level }]

    children.forEach((child) => {
      result.push(...buildHierarchy(child, level + 1))
    })

    return result
  }

  const organized: any[] = []
  rootTypes.forEach((root) => {
    organized.push(...buildHierarchy(root))
  })

  return organized
})

function getAllParentIds(typeId: string): string[] {
  const parentIds: string[] = []
  const entityType = formsStore.entityTypes?.find((type) => type.id === typeId)

  if (entityType && entityType.parentId) {
    parentIds.push(entityType.parentId)
    parentIds.push(...getAllParentIds(entityType.parentId))
  }

  return parentIds
}

function getAllChildIds(typeId: string): string[] {
  const childIds: string[] = []
  const directChildren = formsStore.entityTypes?.filter((type) => type.parentId === typeId) || []

  directChildren.forEach((child) => {
    childIds.push(child.id)
    childIds.push(...getAllChildIds(child.id))
  })

  return childIds
}

function toggleEntityType(typeId: string) {
  const index = store.institution?.typeIds.indexOf(typeId) ?? -1

  if (index > -1) {
    // Deselect: Remove this type and all its children
    const typesToRemove = [typeId, ...getAllChildIds(typeId)]
    typesToRemove.forEach((id) => {
      const idx = store.institution?.typeIds.indexOf(id) ?? -1
      if (idx > -1) {
        store.institution?.typeIds.splice(idx, 1)
      }
    })

    // Make API calls to remove all types
    const removePromises = typesToRemove
        .filter((id) => !store.institution?.typeIds.includes(id) || id === typeId)
        .map((id) => store.removeInstitutionType({typeId: id, institutionId: store.institution?.id}))

    Promise.all(removePromises).then(() => {
      fetch()
    })
  } else {
    // Select: Add this type, all its parents, and all its children
    const typesToAdd = [typeId, ...getAllParentIds(typeId), ...getAllChildIds(typeId)]
    const newTypes = typesToAdd.filter((id) => !store.institution?.typeIds.includes(id))

    newTypes.forEach((id) => {
      store.institution?.typeIds.push(id)
    })

    // Make API calls to add all new types
    const addPromises = newTypes.map((id) =>
        store.setInstitutionType({typeId: id, institutionId: store.institution?.id})
    )

    Promise.all(addPromises).then(() => {
      fetch()
    })
  }
}

function isTypeSelected(typeId: string) {
  return store.institution?.typeIds.includes(typeId)
}

function enableDirectAdmission(){
  showEnableDirectAdmissionConfirm.value = true
}

function confirmEnableDirectAdmission(){
  enablingDirectAdmission.value = true
  store.enableDirectAdmission({institutionId: store.institution?.id})
      .then(() => {
        enablingDirectAdmission.value = false
        showEnableDirectAdmissionConfirm.value = false
        notify.success("Direct admission enabled successfully")
        fetch()
      })
      .catch(() => {
        enablingDirectAdmission.value = false
        showEnableDirectAdmissionConfirm.value = false
        notify.error("Failed to enable direct admission")
      })
}

function disableDirectAdmission(){
  showDisableDirectAdmissionConfirm.value = true
}

function confirmDisableDirectAdmission(){
  disablingDirectAdmission.value = true
  store.disableDirectAdmission({institutionId: store.institution?.id})
      .then(() => {
        disablingDirectAdmission.value = false
        showDisableDirectAdmissionConfirm.value = false
        notify.success("Direct admission disabled successfully")
        fetch()
      })
      .catch(() => {
        disablingDirectAdmission.value = false
        showDisableDirectAdmissionConfirm.value = false
        notify.error("Failed to disable direct admission")
      })
}

function enableInterimErn() {
  showEnableInterimErnConfirm.value = true;
}

function confirmEnableInterimErn() {
  enablingInterimErn.value = true;
  store.enableInterimErn({ institutionId: store.institution?.id })
    .then(() => {
      enablingInterimErn.value = false;
      showEnableInterimErnConfirm.value = false;
      notify.success("Interim ERN enabled successfully");
      fetch();
    })
    .catch(() => {
      enablingInterimErn.value = false;
      showEnableInterimErnConfirm.value = false;
      notify.error("Failed to enable interim ERN");
    });
}

function disableInterimErn() {
  showDisableInterimErnConfirm.value = true;
}

function confirmDisableInterimErn() {
  disablingInterimErn.value = true;
  store.disableInterimErn({ institutionId: store.institution?.id })
    .then(() => {
      disablingInterimErn.value = false;
      showDisableInterimErnConfirm.value = false;
      notify.success("Interim ERN disabled successfully");
      fetch();
    })
    .catch(() => {
      disablingInterimErn.value = false;
      showDisableInterimErnConfirm.value = false;
      notify.error("Failed to disable interim ERN");
    });
}

function enableRegisterExisting() {
  showEnableRegisterExistingConfirm.value = true;
}

function confirmEnableRegisterExisting() {
  enablingRegisterExisting.value = true;
  store.enableRegisterExisting({ institutionId: store.institution?.id })
    .then(() => {
      enablingRegisterExisting.value = false;
      showEnableRegisterExistingConfirm.value = false;
      notify.success("Register affiliations enabled successfully");
      fetch();
    })
    .catch(() => {
      enablingRegisterExisting.value = false;
      showEnableRegisterExistingConfirm.value = false;
      notify.error("Failed to enable register affiliations");
    });
}

function disableRegisterExisting() {
  showDisableRegisterExistingConfirm.value = true;
}

function confirmDisableRegisterExisting() {
  disablingRegisterExisting.value = true;
  store.disableRegisterExisting({ institutionId: store.institution?.id })
    .then(() => {
      disablingRegisterExisting.value = false;
      showDisableRegisterExistingConfirm.value = false;
      notify.success("Register affiliations disabled successfully");
      fetch();
    })
    .catch(() => {
      disablingRegisterExisting.value = false;
      showDisableRegisterExistingConfirm.value = false;
      notify.error("Failed to disable register affiliations");
    });
}

function selectTab(id: string) {
  activeTab.value = id
  if (id === 'compliance-statuses') loadComplianceStatuses()
}

function loadComplianceStatuses() {
  loadingComplianceStatuses.value = true
  registryStore.fetchComplianceStatuses(route.params.id as string, 1, 1000)
    .then(() => { loadingComplianceStatuses.value = false })
}

function openAddComplianceStatus() {
  editingComplianceStatusId.value = null
  complianceStatusForm.value = { status: '', description: '' }
  showComplianceStatusForm.value = true
}

function openEditComplianceStatus(cs: any) {
  editingComplianceStatusId.value = cs.id
  complianceStatusForm.value = { status: cs.status, description: cs.description }
  showComplianceStatusForm.value = true
}

function saveComplianceStatus() {
  savingComplianceStatus.value = true
  const institutionId = route.params.id as string
  const promise = editingComplianceStatusId.value
    ? registryStore.updateComplianceStatus({ id: editingComplianceStatusId.value, ...complianceStatusForm.value })
    : registryStore.createComplianceStatus({ institutionId, ...complianceStatusForm.value })

  promise
    .then(() => {
      savingComplianceStatus.value = false
      showComplianceStatusForm.value = false
      notify.success(editingComplianceStatusId.value ? 'Compliance status updated' : 'Compliance status created')
      loadComplianceStatuses()
    })
    .catch(() => {
      savingComplianceStatus.value = false
      notify.error('Failed to save compliance status')
    })
}

function openAddAttachment() {
  editingAttachment.value = null
  attachmentForm.value = { label: '', description: '', isMandatory: false, maxSize: 5, fileType: '' }
  showAttachmentForm.value = true
}

function openEditAttachment(attachment: any) {
  editingAttachment.value = attachment.id
  attachmentForm.value = {
    label: attachment.label,
    description: attachment.description,
    isMandatory: attachment.isMandatory,
    maxSize: attachment.maxSize,
    fileType: attachment.fileType,
  }
  showAttachmentForm.value = true
}

function cancelAttachmentForm() {
  showAttachmentForm.value = false
  editingAttachment.value = null
}

function saveAttachment() {
  savingAttachment.value = true
  const institutionId = store.institution?.id
  if (editingAttachment.value) {
    formsStore.updateAffiliationFormAttachment({ id: editingAttachment.value, institutionId, ...attachmentForm.value })
      .then(() => {
        savingAttachment.value = false
        showAttachmentForm.value = false
        editingAttachment.value = null
        notify.success("Attachment updated successfully")
        formsStore.fetchAffiliationAttachments(route.params.id as string)
      })
      .catch(() => {
        savingAttachment.value = false
        notify.error("Failed to update attachment")
      })
  } else {
    formsStore.createAffiliationFormAttachment({ institutionId, ...attachmentForm.value })
      .then(() => {
        savingAttachment.value = false
        showAttachmentForm.value = false
        notify.success("Attachment added successfully")
        formsStore.fetchAffiliationAttachments(route.params.id as string)
      })
      .catch(() => {
        savingAttachment.value = false
        notify.error("Failed to add attachment")
      })
  }
}

function removeAttachment(id: string) {
  removingAttachmentId.value = id
  formsStore.removeAffiliationFormAttachment({ id })
    .then(() => {
      removingAttachmentId.value = null
      notify.success("Attachment removed successfully")
      formsStore.fetchAffiliationAttachments(route.params.id as string)
    })
    .catch(() => {
      removingAttachmentId.value = null
      notify.error("Failed to remove attachment")
    })
}

</script>

<template>
  <div class="flex flex-col">

    <div v-if="loading" class="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
      <div class="bg-primary h-full animate-pulse" style="width: 100%"></div>
    </div>

    <div class="flex flex-col gap-4" v-else>
      <div class="flex flex-col gap-4" v-if="store.institution">

        <!-- Register Details + Approve -->
        <div class="bg-white rounded-lg border border-gray-200">
          <div class="flex items-start justify-between px-4 pt-4 pb-3 border-b border-gray-100">
            <div>
              <h2 class="text-lg font-semibold text-gray-900">{{ store.institution.name }}</h2>
              <span class="text-xs text-gray-500">{{ store.institution.code }}</span>
            </div>
            <button v-if="store.institution.approvedAt === null" @click="approve" class="btn-success">
              <i class="fa fa-check mr-2"></i>Approve
            </button>
          </div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-0 divide-x divide-gray-100">
            <div class="px-4 py-3">
              <p class="text-xs text-gray-500 mb-0.5">Status</p>
              <span :class="['px-2 py-0.5 text-xs rounded-full font-medium', store.institution.approvedAt ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800']">
                {{ store.institution.status }}
              </span>
            </div>
            <div class="px-4 py-3">
              <p class="text-xs text-gray-500 mb-0.5">Added On</p>
              <p class="text-sm text-gray-800">{{ dateTimeFormat(store.institution.createdAt) }}</p>
            </div>
            <div v-if="store.institution.approvedAt" class="px-4 py-3">
              <p class="text-xs text-gray-500 mb-0.5">Approved At</p>
              <p class="text-sm text-gray-800">{{ dateTimeFormat(store.institution.approvedAt) }}</p>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="bg-white rounded-t-lg border border-gray-200">
          <div class="flex border-b border-gray-200">
            <label
              v-for="tab in [{id:'contact',label:'Contact Details',icon:'fa-address-card'},{id:'entity-types',label:'Entity Types',icon:'fa-layer-group'},{id:'configurations',label:'Configurations',icon:'fa-sliders'},{id:'affiliation-attachments',label:'Affiliation Attachments',icon:'fa-paperclip'},{id:'compliance-statuses',label:'Compliance Status Configuration',icon:'fa-shield-halved'}]"
              :key="tab.id"
              @click="selectTab(tab.id)"
              :class="['px-5 py-3 text-sm font-medium border-b-2 transition-colors', activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700']">
              <i :class="['fa-solid mr-2', tab.icon]"></i>{{ tab.label }}
            </label>
          </div>

          <!-- Contact Details Tab -->
          <div v-if="activeTab === 'contact'" class="p-4">
            <table class="w-full">
              <tbody>
              <tr class="border-b border-gray-100">
                <td class="py-3 text-gray-500 text-sm w-1/4">Address</td>
                <td class="py-3 text-sm text-gray-800">{{ store.institution.address }}</td>
              </tr>
              <tr v-if="store.institution.contactPersonName" class="border-b border-gray-100">
                <td class="py-3 text-gray-500 text-sm">Contact Person</td>
                <td class="py-3 text-sm text-gray-800">{{ store.institution.contactPersonName }}</td>
              </tr>
              <tr v-if="store.institution.email" class="border-b border-gray-100">
                <td class="py-3 text-gray-500 text-sm">Email</td>
                <td class="py-3 text-sm text-gray-800">{{ store.institution.email }}</td>
              </tr>
              <tr v-if="store.institution.phone" class="border-b border-gray-100">
                <td class="py-3 text-gray-500 text-sm">Phone</td>
                <td class="py-3 text-sm text-gray-800">{{ store.institution.phone }}</td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Entity Types Tab -->
          <div v-if="activeTab === 'entity-types'" class="p-4">
            <div class="border border-gray-200 rounded-lg bg-gray-50 overflow-y-auto max-h-80">
              <div v-if="organizedEntityTypes.length > 0" class="space-y-0.5 p-2">
                <template
                  v-for="entityType in organizedEntityTypes"
                  :key="entityType.id"
                >
                  <div
                    v-if="formsStore.availableEntityTypes?.some(a => a.id === entityType.id) || isTypeSelected(entityType.id)"
                    class="flex items-center p-2 rounded transition hover:bg-white cursor-pointer"
                    @click="toggleEntityType(entityType.id)"
                  >
                    <input
                      type="checkbox"
                      :checked="isTypeSelected(entityType.id)"
                      class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                      @click.stop="toggleEntityType(entityType.id)"
                    />
                    <span
                      class="ml-2 text-sm text-gray-700 flex items-center"
                      :style="{ paddingLeft: `${entityType.level * 20}px` }">
                      <i v-if="entityType.level > 0" class="fa-solid fa-arrow-turn-up fa-rotate-90 text-gray-400 mr-2 text-xs"></i>
                      {{ entityType.name }}
                    </span>
                  </div>
                </template>
              </div>
              <div v-else class="text-sm text-gray-500 text-center py-6">
                <i class="fa-solid fa-info-circle mr-1"></i>No entity types available
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-2">
              <i class="fa-solid fa-info-circle mr-1"></i>
              Select the entity types that this institution can manage.
              <span v-if="store.institution.typeIds.length > 0" class="font-medium text-primary-600">({{ store.institution.typeIds.length }} selected)</span>
            </p>
          </div>

          <!-- Configurations Tab -->
          <div v-if="activeTab === 'configurations'" class="divide-y divide-gray-100">

            <!-- Direct Admission -->
            <div class="flex items-center justify-between px-5 py-4">
              <div>
                <p class="text-sm font-medium text-gray-900">Direct Admission</p>
                <p class="text-xs text-gray-500 mt-0.5">Entities submitted by this register are directly admitted without requiring application processing.</p>
              </div>
              <div class="ml-6 shrink-0">
                <button
                  v-if="!store.institution.hasDirectAdmission"
                  @click="enableDirectAdmission"
                  class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700">
                  <i class="fa fa-bolt mr-2"></i>Enable
                </button>
                <button
                  v-else
                  @click="disableDirectAdmission"
                  class="px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded-md hover:bg-gray-600">
                  <i class="fa fa-ban mr-2"></i>Disable
                </button>
              </div>
            </div>

            <!-- Interim ERN -->
            <div class="flex items-center justify-between px-5 py-4">
              <div>
                <p class="text-sm font-medium text-gray-900">Interim ERN</p>
                <p class="text-xs text-gray-500 mt-0.5">This register can issue interim Entity Registration Numbers before full verification is complete.</p>
              </div>
              <div class="ml-6 shrink-0">
                <button
                  v-if="!store.institution.hasInterimErn"
                  @click="enableInterimErn"
                  class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700">
                  <i class="fa fa-id-card mr-2"></i>Enable
                </button>
                <button
                  v-else
                  @click="disableInterimErn"
                  class="px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded-md hover:bg-gray-600">
                  <i class="fa fa-ban mr-2"></i>Disable
                </button>
              </div>
            </div>

            <!-- Register Affiliations -->
            <div class="flex items-center justify-between px-5 py-4">
              <div>
                <p class="text-sm font-medium text-gray-900">Register Affiliations</p>
                <p class="text-xs text-gray-500 mt-0.5">This register can link and manage affiliations with existing registers in the system.</p>
              </div>
              <div class="ml-6 shrink-0">
                <button
                  v-if="!store.institution.registerExisting"
                  @click="enableRegisterExisting"
                  class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700">
                  <i class="fa fa-link mr-2"></i>Enable
                </button>
                <button
                  v-else
                  @click="disableRegisterExisting"
                  class="px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded-md hover:bg-gray-600">
                  <i class="fa fa-unlink mr-2"></i>Disable
                </button>
              </div>
            </div>

          </div>

          <!-- Compliance Status Configuration Tab -->
          <div v-if="activeTab === 'compliance-statuses'" class="p-4">
            <div class="flex justify-end mb-3">
              <button @click="openAddComplianceStatus" class="btn-primary text-sm">
                <i class="fa fa-plus mr-2"></i>Add Status
              </button>
            </div>
            <div v-if="loadingComplianceStatuses" class="py-8 text-center text-gray-400">
              <i class="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
              <p class="text-sm">Loading compliance statuses...</p>
            </div>
            <div v-else class="bg-white shadow flex flex-col">
              <div class="overflow-x-auto flex-1">
                <table class="w-full text-xs">
                  <thead class="bg-gray-50 sticky top-0">
                    <tr>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Description</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Created At</th>
                      <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(cs, idx) in registryStore.complianceStatuses"
                      :key="cs.id"
                      class="hover:bg-gray-50 transition">
                      <td class="py-2 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
                      <td class="py-2 px-4 border-b border-gray-200">
                        <span class="font-semibold px-2 py-1 rounded-full bg-primary-100 text-primary text-xs">{{ cs.status }}</span>
                      </td>
                      <td class="py-2 px-4 border-b border-gray-200 text-gray-600">{{ cs.description || '—' }}</td>
                      <td class="py-2 px-4 border-b border-gray-200 text-gray-500">{{ dateTimeFormat(cs.createdAt) }}</td>
                      <td class="py-2 px-4 border-b border-gray-200 text-center">
                        <button @click="openEditComplianceStatus(cs)" class="btn-sm-primary">
                          <i class="fa fa-pencil mr-1"></i>Edit
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!registryStore.complianceStatuses || registryStore.complianceStatuses.length === 0">
                      <td colspan="5" class="py-12 text-center">
                        <div class="flex flex-col items-center justify-center text-gray-500">
                          <i class="fa-solid fa-shield-halved text-4xl mb-3 text-gray-300"></i>
                          <p class="text-sm font-medium">No compliance statuses configured</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Affiliation Attachments Tab -->
          <div v-if="activeTab === 'affiliation-attachments'" class="p-4">

            <!-- Header -->
            <div class="flex justify-end mb-3">
              <button @click="openAddAttachment" class="btn-primary text-sm">
                <i class="fa fa-plus mr-2"></i>Add Attachment
              </button>
            </div>

            <!-- Attachments list -->
            <div class="bg-white shadow flex flex-col">
              <div class="overflow-x-auto flex-1">
                <table class="w-full text-xs">
                  <thead class="bg-gray-50 sticky top-0">
                    <tr>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Label</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Description</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">File Type</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Max Size</th>
                      <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Mandatory</th>
                      <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(att, idx) in formsStore.affiliationAttachments" :key="att.id" class="hover:bg-gray-50 transition">
                      <td class="py-2 px-4 border-b border-gray-200">{{ idx + 1 }}</td>
                      <td class="py-2 px-4 border-b border-gray-200 font-bold">{{ att.label }}</td>
                      <td class="py-2 px-4 border-b border-gray-200">{{ att.description || '—' }}</td>
                      <td class="py-2 px-4 border-b border-gray-200">
                        <span class="bg-primary-100 rounded-lg text-primary p-1">
                          {{ availableFileTypes.find(f => f.value === att.fileType)?.label ?? att.fileType }}
                        </span>
                      </td>
                      <td class="py-2 px-4 border-b border-gray-200">{{ att.maxSize }} MB</td>
                      <td class="py-2 px-4 border-b border-gray-200">
                        <span class="px-3 py-1 rounded-lg text-xs" :class="att.isMandatory ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'">
                          {{ att.isMandatory ? 'Mandatory' : 'Optional' }}
                        </span>
                      </td>
                      <td class="py-2 px-4 border-b border-gray-200">
                        <div class="flex gap-2 items-center justify-center">
                          <button @click="openEditAttachment(att)" class="btn-sm-primary">
                            <i class="fa fa-pencil mr-1"></i>Edit
                          </button>
                          <button
                            @click="removeAttachment(att.id)"
                            :disabled="removingAttachmentId === att.id"
                            class="btn-sm-danger disabled:opacity-50">
                            <span v-if="removingAttachmentId === att.id">Removing...</span>
                            <span v-else><i class="fa fa-trash mr-1"></i>Remove</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!formsStore.affiliationAttachments || formsStore.affiliationAttachments.length === 0">
                      <td colspan="7" class="py-12 text-center">
                        <div class="flex flex-col items-center justify-center text-gray-500">
                          <i class="fa-solid fa-paperclip text-4xl mb-3 text-gray-300"></i>
                          <p class="text-sm font-medium">No attachment requirements configured yet</p>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>

        </div>

      </div>

      <div v-else class="text-center py-8 text-gray-500">
        No institution data found
      </div>
    </div>
  </div>

  <AppModal v-model="showApproveConfirm" title="Confirm Approval">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to approve this institution?</p>
      <p class="text-sm text-gray-500 mb-6">This action will grant the institution access to the system.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showApproveConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmApprove"
            :disabled="approving"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
          <span v-if="approving">Approving...</span>
          <span v-else>Confirm Approve</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showRejectConfirm" title="Confirm Rejection">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to reject this institution?</p>
      <p class="text-sm text-red-500 mb-6">This action will deny the institution access to the system.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showRejectConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmReject"
            :disabled="rejecting"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50">
          <span v-if="rejecting">Rejecting...</span>
          <span v-else>Confirm Reject</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showEnableDirectAdmissionConfirm" title="Enable Direct Admission">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to enable direct admission for this register?</p>
      <p class="text-sm text-primary mb-6">Entities submitted by this register will be directly admitted to the non-individual registers without requiring application processing.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showEnableDirectAdmissionConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmEnableDirectAdmission"
            :disabled="enablingDirectAdmission"
            class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700 disabled:opacity-50">
          <span v-if="enablingDirectAdmission">Enabling...</span>
          <span v-else>Confirm Enable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showDisableDirectAdmissionConfirm" title="Disable Direct Admission">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to disable direct admission for this register?</p>
      <p class="text-sm text-gray-500 mb-6">Entities submitted by this register will require application processing before being admitted to the non-individual registers.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showDisableDirectAdmissionConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmDisableDirectAdmission"
            :disabled="disablingDirectAdmission"
            class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
          <span v-if="disablingDirectAdmission">Disabling...</span>
          <span v-else>Confirm Disable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showEnableInterimErnConfirm" title="Enable Interim ERN">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to enable interim ERN for this register?</p>
      <p class="text-sm text-primary mb-6">This register will be allowed to issue interim Entity Registration Numbers
        before full verification is complete.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showEnableInterimErnConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmEnableInterimErn"
            :disabled="enablingInterimErn"
            class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700 disabled:opacity-50">
          <span v-if="enablingInterimErn">Enabling...</span>
          <span v-else>Confirm Enable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showDisableInterimErnConfirm" title="Disable Interim ERN">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to disable interim ERN for this register?</p>
      <p class="text-sm text-gray-500 mb-6">This register will no longer be able to issue interim Entity Registration
        Numbers.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showDisableInterimErnConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmDisableInterimErn"
            :disabled="disablingInterimErn"
            class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
          <span v-if="disablingInterimErn">Disabling...</span>
          <span v-else>Confirm Disable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showEnableRegisterExistingConfirm" title="Enable Register Affiliations">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to enable register affiliations for this register?</p>
      <p class="text-sm text-primary mb-6">This register will be able to link and manage affiliations with existing registers in the system.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showEnableRegisterExistingConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmEnableRegisterExisting"
            :disabled="enablingRegisterExisting"
            class="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-700 disabled:opacity-50">
          <span v-if="enablingRegisterExisting">Enabling...</span>
          <span v-else>Confirm Enable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <AppModal v-model="showDisableRegisterExistingConfirm" title="Disable Register Affiliations">
    <div class="text-center">
      <p class="mb-4">Are you sure you want to disable register affiliations for this register?</p>
      <p class="text-sm text-gray-500 mb-6">This register will no longer be able to link or manage affiliations with existing registers.</p>
      <div class="flex justify-center gap-3">
        <button
            @click="showDisableRegisterExistingConfirm = false"
            class="btn-danger">
          <i class="fas fa-times mr-1"></i> Cancel
        </button>
        <button
            @click="confirmDisableRegisterExisting"
            :disabled="disablingRegisterExisting"
            class="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50">
          <span v-if="disablingRegisterExisting">Disabling...</span>
          <span v-else>Confirm Disable</span>
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Compliance Status Form Modal -->
  <AppModal v-model="showComplianceStatusForm" :title="editingComplianceStatusId ? 'Edit Compliance Status' : 'Add Compliance Status'" hide-footer>
    <div class="p-4 space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Status <span class="text-red-500">*</span>
        </label>
        <input
          v-model="complianceStatusForm.status"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="e.g. COMPLIANT"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          v-model="complianceStatusForm.description"
          rows="3"
          class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          placeholder="Brief description of this status"
        ></textarea>
      </div>
      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button @click="showComplianceStatusForm = false" class="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100">
          Cancel
        </button>
        <button
          @click="saveComplianceStatus"
          :disabled="savingComplianceStatus || !complianceStatusForm.status"
          class="btn-primary disabled:opacity-50">
          <span v-if="savingComplianceStatus">Saving...</span>
          <span v-else>{{ editingComplianceStatusId ? 'Update' : 'Save' }}</span>
        </button>
      </div>
    </div>
  </AppModal>

  <!-- Affiliation Attachment Form Modal -->
  <AppModal v-model="showAttachmentForm" :title="editingAttachment ? 'Edit Attachment' : 'Add Attachment'" hide-footer>
    <div class="p-4 space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          <i class="fa-solid fa-tag text-gray-400 mr-1"></i>
          Label <span class="text-red-500">*</span>
        </label>
        <input
            v-model="attachmentForm.label"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="e.g. Certificate of Incorporation"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
          Description
        </label>
        <textarea
            v-model="attachmentForm.description"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            placeholder="Brief description of what this attachment is"
        ></textarea>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          <i class="fa-solid fa-file-arrow-up text-gray-400 mr-1"></i>
          Maximum File Size (MB) <span class="text-red-500">*</span>
        </label>
        <input
            v-model.number="attachmentForm.maxSize"
            type="number"
            min="1"
            max="50"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          <i class="fa-solid fa-file-lines text-gray-400 mr-1"></i>
          File Type <span class="text-red-500">*</span>
        </label>
        <select
            v-model="attachmentForm.fileType"
            class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">-- Select file type --</option>
          <option v-for="ft in availableFileTypes" :key="ft.value" :value="ft.value">
            {{ ft.label }}
          </option>
        </select>
        <p class="text-xs text-gray-500 mt-1">Select the allowed file type for this attachment</p>
      </div>

      <div class="flex items-center gap-2">
        <input
            id="affAttachmentMandatory"
            v-model="attachmentForm.isMandatory"
            type="checkbox"
            class="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
        />
        <label for="affAttachmentMandatory" class="text-sm text-gray-700">Mandatory</label>
      </div>

      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button @click="cancelAttachmentForm" class="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100">
          Cancel
        </button>
        <button
            @click="saveAttachment"
            :disabled="savingAttachment || !attachmentForm.label || !attachmentForm.fileType"
            class="btn-primary disabled:opacity-50">
          <span v-if="savingAttachment">Saving...</span>
          <span v-else>{{ editingAttachment ? 'Update' : 'Save' }}</span>
        </button>
      </div>
    </div>
  </AppModal>
</template>

<style scoped>

</style>
